import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { STATUS_API } from 'src/app/constant/config';
import { MESSAGE } from 'src/app/constant/message';
import {
  applyAdminOverride,
  clearOsmLookup,
  createAdminOverride,
  getAdminOverrides,
  getHereSyncTargets,
  lookupOsmGeometry,
  revertAdminOverride
} from 'src/features/hereSlice';

const EMPTY = { key: '', name: '', ward: '', province: '', note: '', osmType: 'R', osmId: '' };

const STATUS_LABEL = { draft: 'Nháp', applied: 'Đang áp dụng', failed: 'Lỗi' };

const formatTime = value => (value ? new Date(value).toLocaleString('vi-VN') : '—');

/**
 * Khối "Sửa đơn vị hành chính".
 *
 * Đây là NGOẠI LỆ có cân nhắc của nguyên tắc "không đụng đơn vị hành chính", và ranh giới của
 * ngoại lệ nằm ở nguồn dữ liệu: khối này KHÔNG nhận gì từ HERE. HERE sai toàn bộ 42/42 điểm về
 * cấp hành chính sau sáp nhập 01/7/2025. Chỗ này chỉ dùng khi chính OSM vẽ sai ranh giới, và mỗi
 * vùng bắt buộc kèm căn cứ pháp lý.
 *
 * Vùng khai theo id OSM chứ không dán toạ độ: khu vực cần sửa gần như luôn đã có sẵn trong OSM
 * (khu công nghiệp, khu dân cư, phường), và tra theo id thì còn truy được vùng lấy từ đâu.
 */
const AdminOverridePanel = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const overrides = useSelector(state => state.hereSlice.adminOverrides);
  const targets = useSelector(state => state.hereSlice.targets);
  const osmLookup = useSelector(state => state.hereSlice.osmLookup);
  const statusList = useSelector(state => state.hereSlice.statusGetAdminOverrides);
  const statusLookup = useSelector(state => state.hereSlice.statusLookupOsm);
  const statusMutate = useSelector(state => state.hereSlice.statusMutateAdminOverride);

  const [form, setForm] = useState(EMPTY);
  const [openForm, setOpenForm] = useState(false);
  const [confirmApply, setConfirmApply] = useState(null);
  const [targetId, setTargetId] = useState('');

  const reload = useCallback(() => dispatch(getAdminOverrides()), [dispatch]);

  useEffect(() => {
    reload();
    dispatch(getHereSyncTargets());
  }, [reload, dispatch]);

  useEffect(() => {
    if (!targetId && targets.length) setTargetId(targets[0].id);
  }, [targets, targetId]);

  useEffect(() => {
    if (statusMutate === STATUS_API.SUCCESS) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusMutate]);

  const openCreate = () => {
    setForm(EMPTY);
    dispatch(clearOsmLookup());
    setOpenForm(true);
  };

  const lookup = () => dispatch(lookupOsmGeometry({ osmType: form.osmType, osmId: form.osmId }));

  const submit = () => {
    dispatch(
      createAdminOverride({
        key: form.key.trim(),
        name: form.name.trim() || osmLookup?.name || '',
        ward: form.ward.trim() || undefined,
        province: form.province.trim() || undefined,
        note: form.note.trim(),
        geojson: osmLookup?.geojson
      })
    );
    setOpenForm(false);
  };

  // Chưa tra được vùng thì không cho lưu: bản ghi thiếu hình học sẽ bị dịch vụ từ chối, nhưng để
  // người dùng bấm rồi mới báo lỗi là bắt họ điền lại cả biểu mẫu.
  const canSubmit =
    form.key.trim() && form.note.trim().length >= 20 && (form.ward.trim() || form.province.trim()) && osmLookup?.geojson;

  const busy = statusMutate === STATUS_API.PENDING;

  return (
    <Card className={classes.card}>
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap">
          <Box>
            <Typography variant="h5">Sửa đơn vị hành chính</Typography>
            <Typography variant="body2" color="textSecondary">
              Dùng khi ranh giới trong OpenStreetMap vẽ sai so với văn bản pháp lý. Sửa theo VÙNG, không theo từng điểm.
              Không lấy dữ liệu từ HERE — HERE chưa cập nhật sáp nhập 01/7/2025.
            </Typography>
          </Box>
          <Box display="flex" gridGap={8} mt={1}>
            <FormControl variant="outlined" size="small" className={classes.select}>
              <InputLabel>Server bản đồ</InputLabel>
              <Select label="Server bản đồ" value={targetId} onChange={event => setTargetId(event.target.value)}>
                {targets.map(target => (
                  <MenuItem key={target.id} value={target.id}>
                    {target.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" color="primary" onClick={openCreate}>
              Thêm vùng
            </Button>
          </Box>
        </Box>

        <Divider className={classes.divider} />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Vùng</TableCell>
              <TableCell>Sửa thành</TableCell>
              <TableCell>Căn cứ</TableCell>
              <TableCell>Trạng thái</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {overrides.map(item => (
              <TableRow key={item.id} hover>
                <TableCell>
                  {item.name}
                  <Typography variant="caption" display="block" color="textSecondary">
                    {item.key}
                  </Typography>
                </TableCell>
                <TableCell>
                  <span className={classes.changed}>{[item.ward, item.province].filter(Boolean).join(', ')}</span>
                </TableCell>
                <TableCell className={classes.note}>{item.note}</TableCell>
                <TableCell>
                  {STATUS_LABEL[item.status] || item.status}
                  {item.applied_by && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      {item.applied_by} · {formatTime(item.applied_at)}
                    </Typography>
                  )}
                  {item.last_error && (
                    <Typography variant="caption" display="block" className={classes.error}>
                      {item.last_error}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {item.status === 'applied' ? (
                    <Button size="small" disabled={busy} onClick={() => dispatch(revertAdminOverride(item.id))}>
                      Gỡ
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      color="primary"
                      disabled={busy || !targetId}
                      onClick={() => setConfirmApply(item)}>
                      Áp dụng
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!overrides.length && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {statusList === STATUS_API.PENDING ? 'Đang tải...' : 'Chưa khai vùng nào'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={openForm} onClose={() => setOpenForm(false)} fullWidth maxWidth="md">
        <DialogTitle className={classes.dialogTitle}>
          <Typography variant="h6" className={classes.titleText}>
            Thêm vùng ghi đè hành chính
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" gridGap={8} alignItems="flex-start" mt={1}>
            <TextField
              select
              size="small"
              variant="outlined"
              label="Loại"
              className={classes.osmType}
              value={form.osmType}
              onChange={event => setForm({ ...form, osmType: event.target.value })}>
              <MenuItem value="R">Quan hệ (R)</MenuItem>
              <MenuItem value="W">Đường bao (W)</MenuItem>
            </TextField>
            <TextField
              size="small"
              variant="outlined"
              label="ID đối tượng OSM"
              placeholder="16130747"
              helperText="Mở openstreetmap.org, chọn vùng, lấy số ở cuối địa chỉ"
              value={form.osmId}
              onChange={event => setForm({ ...form, osmId: event.target.value })}
            />
            <Button variant="outlined" color="primary" disabled={!form.osmId || statusLookup === STATUS_API.PENDING} onClick={lookup}>
              {statusLookup === STATUS_API.PENDING ? 'Đang tra...' : 'Tra vùng'}
            </Button>
          </Box>

          {osmLookup && (
            <Box className={classes.lookupBox} mt={1}>
              <Typography variant="body2">
                <strong>{osmLookup.name || '(không tên)'}</strong> · {osmLookup.areaHa} ha · {osmLookup.geojson?.type}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            label="Mã vùng"
            placeholder="kcn-thach-that-quoc-oai"
            helperText="Khai lại cùng mã = cập nhật vùng cũ"
            value={form.key}
            onChange={event => setForm({ ...form, key: event.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            label="Tên hiển thị"
            placeholder={osmLookup?.name || 'Khu công nghiệp...'}
            value={form.name}
            onChange={event => setForm({ ...form, name: event.target.value })}
          />
          <Box display="flex" gridGap={8}>
            <TextField
              fullWidth
              margin="dense"
              size="small"
              variant="outlined"
              label="Xã/phường đúng"
              placeholder="Xã Tây Phương"
              value={form.ward}
              onChange={event => setForm({ ...form, ward: event.target.value })}
            />
            <TextField
              fullWidth
              margin="dense"
              size="small"
              variant="outlined"
              label="Tỉnh/thành (nếu cần sửa)"
              placeholder="Thành phố Hà Nội"
              value={form.province}
              onChange={event => setForm({ ...form, province: event.target.value })}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={3}
            margin="dense"
            size="small"
            variant="outlined"
            label="Căn cứ pháp lý"
            placeholder="NQ 1656/NQ-UBTVQH15 khoản 95..."
            helperText={`Bắt buộc, tối thiểu 20 ký tự — người sau phải kiểm lại được (${form.note.trim().length}/20)`}
            value={form.note}
            onChange={event => setForm({ ...form, note: event.target.value })}
          />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setOpenForm(false)} variant="outlined">
            Hủy
          </Button>
          <Button type="button" variant="contained" color="primary" disabled={!canSubmit} onClick={submit}>
            Lưu
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmApply)} onClose={() => setConfirmApply(null)} fullWidth maxWidth="sm">
        <DialogTitle>{MESSAGE.CONFIRM_APPLY_ADMIN_OVERRIDE}</DialogTitle>
        <DialogContent>
          <Typography>{MESSAGE.CONTENT_APPLY_ADMIN_OVERRIDE}</Typography>
          {confirmApply && (
            <Box mt={2}>
              <Typography variant="body2">
                <strong>{confirmApply.name}</strong> → {[confirmApply.ward, confirmApply.province].filter(Boolean).join(', ')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApply(null)}>Hủy</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              dispatch(applyAdminOverride({ id: confirmApply.id, targetId }));
              setConfirmApply(null);
            }}>
            Áp dụng
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const useStyles = makeStyles(theme => ({
  card: { marginTop: theme.spacing(2) },
  divider: { margin: `${theme.spacing(2)}px 0` },
  select: { minWidth: 180 },
  osmType: { minWidth: 140 },
  changed: { fontWeight: 600, color: theme.palette.success?.main || theme.palette.primary.main },
  // Căn cứ thường dài; giới hạn bề ngang để cột trạng thái không bị đẩy khỏi màn hình.
  note: { maxWidth: 380, fontSize: 12, color: theme.palette.text.secondary },
  error: { color: theme.palette.error.main },
  lookupBox: { background: '#e8f5e9', borderRadius: 4, padding: theme.spacing(1, 1.5) },
  dialogTitle: { padding: theme.spacing(2, 3), borderBottom: '1px solid rgba(0, 0, 0, 0.12)' },
  titleText: { fontSize: '18px', fontWeight: 600 },
  dialogActions: { padding: theme.spacing(2, 3) }
}));

export default AdminOverridePanel;
